import React, { createContext, useContext, useState, ReactNode } from 'react';

type SelectedCategories = {
  mealType: string[];
  cuisineStyle: string[];
  mainIngredients: string[];
  vegetablesFruits: string[];
  grainsStaples: string[];
  dietaryPreferences: string[];
  cookingMethods: string[];
  additionalModifiers: string[];
};

type PopularRecipesCache = {
  recipes: any[];
  timestamp: number;
  expiresAt: number;
};

type RecipeContextType = {
  selectedCategories: SelectedCategories;
  prompt: string;
  results: any[];
  mode: 'generate' | 'regenerate' | 'find' | 'popular';
  popularRecipesCache: PopularRecipesCache | null;
  addChip: (category: keyof SelectedCategories, chip: string) => void;
  removeChip: (category: keyof SelectedCategories, chip: string) => void;
  buildPrompt: () => void;
  setResults: (results: any[]) => void;
  setMode: (mode: 'generate' | 'regenerate' | 'find' | 'popular') => void;
  setPopularRecipesCache: (cache: PopularRecipesCache | null) => void;
  getPopularRecipesCache: () => PopularRecipesCache | null;
  clearPopularRecipesCache: () => void;
  clearAll: () => void;
};

const RecipeContext = createContext<RecipeContextType | undefined>(undefined);

export const useRecipe = () => {
  const context = useContext(RecipeContext);
  if (!context) {
    throw new Error('useRecipe must be used within a RecipeProvider');
  }
  return context;
};

interface RecipeProviderProps {
  children: ReactNode;
}

export const RecipeProvider: React.FC<RecipeProviderProps> = ({ children }) => {
  const [selectedCategories, setSelectedCategories] = useState<SelectedCategories>({
    mealType: [],
    cuisineStyle: [],
    mainIngredients: [],
    vegetablesFruits: [],
    grainsStaples: [],
    dietaryPreferences: [],
    cookingMethods: [],
    additionalModifiers: [],
  });

  const [prompt, setPrompt] = useState<string>('');
  const [results, setResults] = useState<any[]>([]);
  const [mode, setMode] = useState<'generate' | 'regenerate' | 'find' | 'popular'>('generate');
  const [popularRecipesCache, setPopularRecipesCache] = useState<PopularRecipesCache | null>(null);

  const addChip = (category: keyof SelectedCategories, chip: string) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [category]: [...prev[category], chip],
    }));
  };

  const removeChip = (category: keyof SelectedCategories, chip: string) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [category]: prev[category].filter((item) => item !== chip),
    }));
  };

  const buildPrompt = () => {
    const {
      mealType,
      cuisineStyle,
      mainIngredients,
      vegetablesFruits,
      grainsStaples,
      dietaryPreferences,
      cookingMethods,
      additionalModifiers,
    } = selectedCategories;

    let promptStr = 'Generate a';

    if (mealType.length > 0) {
      promptStr += ` ${mealType.join(', ')}`;
    }

    promptStr += ' recipe';

    if (cuisineStyle.length > 0) {
      promptStr += ` in ${cuisineStyle.join(', ')} style`;
    }

    if (mainIngredients.length > 0) {
      promptStr += ` using ${mainIngredients.join(', ')}`;
    }

    if (vegetablesFruits.length > 0) {
      promptStr += ` with ${vegetablesFruits.join(', ')}`;
    }

    if (grainsStaples.length > 0) {
      promptStr += ` and ${grainsStaples.join(', ')}`;
    }

    if (dietaryPreferences.length > 0) {
      promptStr += `, suitable for ${dietaryPreferences.join(', ')}`;
    }

    if (cookingMethods.length > 0) {
      promptStr += `, prepared by ${cookingMethods.join(', ')}`;
    }

    if (additionalModifiers.length > 0) {
      promptStr += `, with ${additionalModifiers.join(', ')}`;
    }

    promptStr += '.';

    setPrompt(promptStr);
  };

  const clearAll = () => {
    setSelectedCategories({
      mealType: [],
      cuisineStyle: [],
      mainIngredients: [],
      vegetablesFruits: [],
      grainsStaples: [],
      dietaryPreferences: [],
      cookingMethods: [],
      additionalModifiers: [],
    });
  };

  const getPopularRecipesCache = (): PopularRecipesCache | null => {
    if (!popularRecipesCache) return null;
    
    // Check if cache is expired
    if (Date.now() > popularRecipesCache.expiresAt) {
      setPopularRecipesCache(null);
      return null;
    }
    
    return popularRecipesCache;
  };

  const clearPopularRecipesCache = () => {
    setPopularRecipesCache(null);
  };

  return (
    <RecipeContext.Provider
      value={{
        selectedCategories,
        prompt,
        results,
        mode,
        popularRecipesCache,
        addChip,
        removeChip,
        buildPrompt,
        setResults,
        setMode,
        setPopularRecipesCache,
        getPopularRecipesCache,
        clearPopularRecipesCache,
        clearAll,
      }}
    >
      {children}
    </RecipeContext.Provider>
  );
};